import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContentByLabelDataQuery$data,
	useContentByLabelDataQuery$variables,
} from './__generated__/useContentByLabelDataQuery.graphql.ts';

interface UseContentByLabelDataResult {
	data: useContentByLabelDataQuery$data | undefined;
	error: Error | null;
	loading: boolean;
}

/**
 * Hook that fetches content matching the given CQL query via the
 * confluence_search GraphQL endpoint.
 *
 * Accepts pre-built query variables from useParsedMacroParams (via
 * macroParamsForQuery), avoiding any reprocessing of macro params here.
 */
export const useContentByLabelData = (
	macroParamsForQuery: useContentByLabelDataQuery$variables,
): UseContentByLabelDataResult => {
	const { cql } = macroParamsForQuery;
	const { data, error, loading } = useQueryInterop<
		useContentByLabelDataQuery$data,
		useContentByLabelDataQuery$variables
	>(
		graphql`
			query useContentByLabelDataQuery(
				$cql: String!
				$first: Int
				$includeExcerpt: Boolean!
				$fallbackToBody: Boolean!
			) {
				search: confluence_search(cql: $cql, first: $first) {
					nodes {
						title
						url
						lastModified
						content {
							id
							type
							subType
							macroExcerpt @include(if: $includeExcerpt) {
								atlasDocFormat(fallbackToBody: $fallbackToBody, maxBlocks: 3)
							}
							body {
								atlas_doc_format @include(if: $includeExcerpt) {
									mediaToken {
										token
									}
								}
							}
							space {
								id
								key
								name
							}
							labels {
								nodes {
									id
									name
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: macroParamsForQuery,
			skip: !cql,
		},
	);

	return {
		data,
		error: error ?? null,
		loading: Boolean(loading),
	};
};
